/**
 * The diff module's server state: revisions to pick from (the same
 * server fact the viewer reads -- same query key, shared cache) and
 * the version diff through the 202-materialization protocol.
 */
import { useQuery } from '@tanstack/react-query';
import { API_BASE, authFetch } from '../../api';

export interface Revision {
  id: number;
  project_id: string;
  number: number;
  snapshot_project_id: string;
  element_count: number;
  open_findings: number;
  label: string;
  created_at: string | null;
}

export interface DiffBucketCounts {
  unchanged: number;
  modified: number;
  deleted: number;
  added: number;
}

export interface VersionDiffResult {
  base_project_id: string;
  revised_project_id: string;
  by_class: Record<string, DiffBucketCounts>;
  totals: DiffBucketCounts;
  unchanged: string[];
  modified: string[];
  deleted: string[];
  added: string[];
}

export function useRevisionList(projectId: string) {
  return useQuery<Revision[]>({
    queryKey: ['revisions', projectId],
    queryFn: async () => {
      const res = await authFetch(
        `${API_BASE}/v1/revisions?project_id=${encodeURIComponent(projectId)}`,
      );
      if (!res.ok) throw new Error(`revisions: ${res.status}`);
      return (await res.json()) ?? [];
    },
  });
}

type DiffState = { queued: true } | { queued: false; result: VersionDiffResult };

const QUEUED_POLL_MS = 3000;

export function useVersionDiff(baseProjectId: string, revisedProjectId: string) {
  return useQuery<DiffState>({
    queryKey: ['diff', baseProjectId, revisedProjectId],
    enabled: Boolean(baseProjectId && revisedProjectId && baseProjectId !== revisedProjectId),
    queryFn: async () => {
      const res = await authFetch(`${API_BASE}/v1/diff/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          base_project_id: baseProjectId,
          revised_project_id: revisedProjectId,
        }),
      });
      if (res.status === 202) return { queued: true };
      if (!res.ok) throw new Error(`diff: ${res.status}`);
      return { queued: false, result: (await res.json()) as VersionDiffResult };
    },
    refetchInterval: (query) => (query.state.data?.queued ? QUEUED_POLL_MS : false),
    refetchIntervalInBackground: true,
  });
}
